import React, { useEffect, useState } from 'react';
import { Loader2, Handshake, ChevronDown, ChevronUp } from 'lucide-react';
import logger from '../../utils/logger';

const STATUS_LABEL = { registered: 'Joined', pending: 'Awaiting reply' };
const STATUS_TONE = {
  registered: 'text-emerald-700 bg-emerald-50 border-emerald-100',
  pending: 'text-slate-600 bg-slate-50 border-slate-200',
};
const COLLAPSED_LIMIT = 3;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch (_) { return iso; }
};

const IntroductionHistory = ({ refreshToken }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${API_URL}/api/users/my-introductions`, {
          headers: { 'Authorization': `Bearer ${token}` }, cache: 'no-store',
        });
        if (r.ok) {
          const data = await r.json();
          setItems(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (err) { logger.error('Error loading introduction history:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [API_URL, refreshToken]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-3" data-testid="intro-history-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your introductions…
      </div>
    );
  }

  return (
    <div className="mt-6 border-t border-slate-200 pt-6" data-testid="intro-history-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-slate-900">Your past introductions</h3>
        <span className="text-xs text-slate-500" data-testid="intro-history-total">
          {total} {total === 1 ? 'introduction' : 'introductions'} sent
        </span>
      </div>
      {total === 0 ? (
        <p className="text-sm text-slate-500" data-testid="intro-history-empty">
          You haven&apos;t introduced anyone yet.
        </p>
      ) : (
        <>
          <ul className="space-y-2" data-testid="intro-history-list">
            {(expanded ? items : items.slice(0, COLLAPSED_LIMIT)).map((it) => (
              <li
                key={it.introduction_id}
                className="flex items-start justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2.5 bg-white"
                data-testid="intro-history-item"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Handshake className="w-4 h-4 text-emerald-700 shrink-0" />
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {it.recipient_name || it.recipient_email}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {it.recipient_email}
                    {it.relationship ? ` • ${it.relationship}` : ''}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span
                    className={`inline-block text-[11px] font-medium border rounded-full px-2 py-0.5 ${STATUS_TONE[it.status] || STATUS_TONE.pending}`}
                    data-testid={`intro-history-status-${it.status}`}
                  >
                    {STATUS_LABEL[it.status] || it.status}
                  </span>
                  <p className="text-[11px] text-slate-500 mt-1">{formatDate(it.created_at)}</p>
                </div>
              </li>
            ))}
          </ul>
          {total > COLLAPSED_LIMIT && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:text-emerald-900 underline underline-offset-2"
              data-testid="intro-history-toggle"
              aria-expanded={expanded}
            >
              {expanded
                ? (<>Show less <ChevronUp className="w-3.5 h-3.5" /></>)
                : (<>Show all ({total}) <ChevronDown className="w-3.5 h-3.5" /></>)}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default IntroductionHistory;
